<!--
  A horizontally scrolling row of posters. Snap-scrolls under a thumb; on a wide
  screen it grows arrows, because a trackpad user should not have to shift-wheel.
  Bleeds to the screen edge on purpose: the cut-off poster is what tells you the
  row continues.

  ── AND IT NEVER ENDS ──────────────────────────────────────────────────────
  The way-back tile is gone (the owner, 2026-08-17, changing his mind on the
  2026-08-15 ask): a rail LOADS at its end instead — the caller keeps handing
  it more of the category through `more()` — and once the category is truly
  finished, the rail loops back to its own beginning with no dead stop.

  Looping, by EITHER mechanism, is licensed by overflow: a loop hides its
  seam beyond the glass, and a rail whose whole sequence FITS its viewport
  has nowhere to hide one — repeating anything would draw the same poster
  twice in plain view (the Top 10 on a 2880px screen). A rail that fits
  simply stands complete; shrink the window until it overflows and it earns
  the loop, widen it back and it folds to a single wrap (`refit`).

  Two mechanisms, chosen by what the rail is:

  • RING MODE (`items` + `tile`): a windowed virtual ring for the deep rails.
    The track renders a bounded slice of an endless tape of positions; the
    tape reads `items[j % count]` once the category is exhausted, and a
    spacer of exactly the recycled columns' width stands in for everything
    dropped off the left. There is no seam anywhere — walking or flinging
    right simply never ends, because the next position always exists — and
    the DOM is capped at RING_CAP tiles however deep the category goes
    (Documentary is 2,644 titles; measured 2026-08-17). The arithmetic is
    `rail-ring.ts`; the only correction ever applied to the scroller is
    `shed()`, which drops whole wraps out of the spacer during a settled
    frame, pixel-compensated, so scrollLeft stays bounded over a long night.

  • COPIES MODE (plain `children`): the short static rails — Continue
    watching, Recently opened, more-like — render their whole sequence a
    bounded number of times (`copiesFor`) once they prove scrollable, and a
    settled frame teleports the scroller back a stride (`teleportWraps`),
    moving the DOM focus to the identical tile in the earlier copy first so
    a remote never notices. The sequences are ≤ ~20 cards, so copies are
    cheaper than a ring's machinery, and the seam crossing itself is real
    DOM continuity — the correction happens later, at rest, on identical
    pixels.

  Each rendered tile is its OWN focus/trailer citizen (`newFocusId` per
  Poster instance), which is what lets the same title exist in two wraps at
  once without the two fighting — the same rule that already let one film sit
  in two rails.
-->
<script lang="ts">
  import type { Snippet } from 'svelte';
  import { flushSync } from 'svelte';
  import type { CatalogItem } from '$types';
  import Icon from './Icon.svelte';
  import Poster from './Poster.svelte';
  import SpotlightStrip from './SpotlightStrip.svelte';
  import {
    LOOP_SETTLE_MS,
    MORE_RETRY_MS,
    RING_WINDOW,
    copiesFor,
    ghostHidden,
    growPlan,
    nearEnd,
    nearSpacer,
    ordinalAt,
    restorePlan,
    shedWraps,
    teleportWraps
  } from './rail-ring';

  interface Props {
    title: string;
    /**
     * A quiet second line under the heading — "because you kept Dune". Home uses
     * it to say WHERE a row came from, because a row that claims to know you and
     * will not say why is the thing everybody hates about these pages.
     */
    note?: string | undefined;
    /**
     * Sixteen-by-nine cards instead of posters — the Continue row. Same rail,
     * same snapping, same arrows, wider column.
     */
    wide?: boolean;
    /**
     * A "Top 10 this week" row: every tile carries an oversized outlined
     * numeral. The caller passes `rank` to each Poster; this only widens the
     * column so the numeral has somewhere to live.
     */
    ranked?: boolean;
    /** Optional "See all"-style link on the right of the heading. */
    href?: string | undefined;
    hrefLabel?: string;
    /**
     * ── THE LOAD-MORE CONTRACT ─────────────────────────────────────────────
     * Called when the scroller approaches its end (a distance threshold, the
     * ghost-sentinel's margin as arithmetic — `nearEnd`). The caller extends
     * its category and answers:
     *
     *   'grew' — more items exist now (in ring mode, `items` is longer; in
     *            children mode, the snippet renders more). The rail will ask
     *            again at the next approach.
     *   'end'  — the category is finished. The rail never asks again and,
     *            if it scrolls at all, becomes a loop.
     *
     * One call in flight at a time; a rejection is treated as transient (the
     * next approach retries) and never as exhaustion. Omitting the prop IS
     * the static-rail answer: exhausted from birth, loop when scrollable.
     */
    more?: (() => Promise<'grew' | 'end'>) | undefined;
    /**
     * RING MODE. The items fetched so far — the rail windows them itself and
     * renders each position through `tile`. Deep rails (LazyRail, AwardRail,
     * Person) use this; a rail whose whole sequence is a dozen cards keeps
     * passing children and gets the copies loop instead.
     */
    items?: CatalogItem[] | undefined;
    /**
     * One rendered position of the ring: the item, and its 1-based ordinal
     * within the sequence — the ranked numeral's number, correct in every
     * wrap because it is `j % count`, never the raw position.
     */
    tile?: Snippet<[CatalogItem, number]> | undefined;
    /**
     * Whether an exhausted, scrollable rail loops. Default yes — the owner's
     * brief. Placeholder rails (a row of ghost cards holding height while a
     * catalogue answers) decline: an endless loop of shimmer is a promise
     * the rail cannot keep.
     */
    loop?: boolean;
    children?: Snippet | undefined;
  }

  let {
    title,
    note,
    wide = false,
    ranked = false,
    href,
    hrefLabel = 'All',
    more,
    items,
    tile,
    loop = true,
    children
  }: Props = $props();

  let track = $state<HTMLDivElement | null>(null);
  let spacerEl = $state<HTMLElement | null>(null);
  let atStart = $state(true);
  let atEndRaw = $state(false);
  /** Whether this rail actually scrolls — the gate on looping at all. */
  let scrollable = $state(false);

  /* ── The contract's state ────────────────────────────────────────────── */

  /** `more()` has answered 'end'. */
  let ended = $state(false);
  /** No `more` prop is the static rail saying "everything is already here". */
  const exhausted = $derived(ended || more === undefined);
  /** A `more()` call is in flight — the ring's ghost card shimmers meanwhile. */
  let asking = $state(false);
  /**
   * A REJECTED ask's ghost, kept mounted. Unmounting the ghost on failure
   * shrinks scrollWidth, and a viewer parked at the rail's end has their
   * scrollLeft clamped backward by a whole column — a visible hop, from a
   * network blip. So the ghost outlives the rejection: it goes only when
   * real tiles replace it in the same flush, or once it is off the glass
   * (`ghostHidden`), where nobody can watch it leave.
   */
  let stale = $state(false);
  /** After a rejection, no re-ask before this — see MORE_RETRY_MS. */
  let coolUntil = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  /* ── Ring mode ───────────────────────────────────────────────────────── */

  const ring = $derived(items !== undefined && tile !== undefined);
  const count = $derived(items?.length ?? 0);
  /** The rendered window of tape positions. See rail-ring.ts. */
  let lo = $state(0);
  let hi = $state(RING_WINDOW);
  /**
   * Columns the spacer stands in for. Tracks `lo` as the window advances and
   * falls back with every restore — except that `shed()` subtracts whole
   * wraps from it WITHOUT touching `lo`, which is why it is its own number.
   */
  let dropped = $state(0);

  const slice = $derived.by(() => {
    if (!ring || count === 0) return [];
    const list = items!;
    const end = exhausted ? hi : Math.min(hi, count);
    const out: { j: number; item: CatalogItem; ordinal: number }[] = [];
    for (let j = lo; j < end; j += 1) {
      out.push({ j, item: list[j % count]!, ordinal: ordinalAt(j, count) });
    }
    return out;
  });

  /* ── Copies mode ─────────────────────────────────────────────────────── */

  let copies = $state(1);
  const copyKeys = $derived(Array.from({ length: copies }, (_, c) => c));

  /**
   * The right arrow never plays dead over a rail that has more to give: while
   * the category is still loading, or once the rail loops, "the end" is not a
   * place the row has.
   */
  const atEnd = $derived(atEndRaw && exhausted && !(loop && scrollable));

  /* ── Measuring, probing, settling ────────────────────────────────────── */

  function measure(): void {
    const el = track;
    if (!el) return;
    atStart = el.scrollLeft < 8;
    atEndRaw = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
    scrollable = el.scrollWidth > el.clientWidth + 8;
  }

  /** The track's computed gap and inline padding, for the one-wrap sums. */
  function trackMetrics(el: HTMLElement): { gap: number; pad: number } {
    const cs = getComputedStyle(el);
    return {
      gap: Number.parseFloat(cs.columnGap) || 0,
      pad:
        (Number.parseFloat(cs.paddingInlineStart) || 0) + (Number.parseFloat(cs.paddingInlineEnd) || 0)
    };
  }

  /**
   * ONE WRAP of the ring's sequence overflows the glass — the licence to
   * loop (see growPlan's `wrap`). A loop hides its seam beyond the viewport;
   * a rail whose whole sequence FITS has nowhere to hide one, and repeating
   * positions would set the same poster beside itself in plain view.
   * Geometry is exact by construction — every column is `--col` wide with a
   * `--gap` after it — so one wrap's width is arithmetic on the first tile's
   * own rectangle, true however many wraps happen to be mounted, which
   * `scrollWidth` (the mounted total) could never tell apart.
   */
  function oneWrapOverflows(): boolean {
    const el = track;
    if (!el || count <= 0 || typeof getComputedStyle !== 'function') return false;
    const first = el.children[dropped > 0 ? 1 : 0] as HTMLElement | undefined; // [0] is the spacer while dropped > 0
    if (!first) return false;
    const { gap, pad } = trackMetrics(el);
    const col = first.getBoundingClientRect().width;
    return pad + count * (col + gap) - gap > el.clientWidth + 8;
  }

  /**
   * ONE COPY of a static rail's sequence overflows the glass — the copies
   * loop's version of the same licence, asked when a resize may have taken
   * it away. Measured between the two copies' first children, like
   * `teleport()`, so an open 16:9 card cannot skew it. Answers true when it
   * cannot tell (a ghost making the child count uneven): unsure keeps the
   * loop, and the next settled resize asks again.
   */
  function copyOverflows(): boolean {
    const el = track;
    if (!el || copies <= 1 || typeof getComputedStyle !== 'function') return true;
    const kids = el.children;
    const n = kids.length;
    if (n === 0 || n % copies !== 0) return true;
    const per = n / copies;
    const stride = kids[per]!.getBoundingClientRect().left - kids[0]!.getBoundingClientRect().left;
    if (stride <= 0) return true;
    const { gap, pad } = trackMetrics(el);
    return pad + stride - gap > el.clientWidth + 8;
  }

  /**
   * growPlan's `wrap`, measured only when it could matter: before exhaustion
   * growth is clamped to the tape anyway, so an unexhausted advance never
   * pays the style-and-rect reads this involves.
   */
  function wrapLicence(): boolean {
    return exhausted && loop && oneWrapOverflows();
  }

  /**
   * The cheap per-scroll checks: grow at the right threshold, restore at the
   * spacer's, engage the copies the first time a static rail is moved. All
   * reads; every write they cause lands in state, never in the scroller.
   */
  function probe(): void {
    const el = track;
    if (!el) return;
    if (ring) {
      if (nearEnd(el.scrollLeft, el.clientWidth, el.scrollWidth)) void advance();
      const sp = spacerEl;
      if (dropped > 0 && sp) {
        const end = sp.getBoundingClientRect().right - el.getBoundingClientRect().left + el.scrollLeft;
        if (nearSpacer(el.scrollLeft, end)) restore();
      }
      return;
    }
    if (!exhausted && nearEnd(el.scrollLeft, el.clientWidth, el.scrollWidth)) void advance();
    if (exhausted && loop && scrollable && copies === 1) engageCopies();
  }

  /** A stale ghost may leave only where nobody can watch it go. */
  function sweepStale(): void {
    const el = track;
    if (!el || !stale || asking) return;
    if (ghostHidden(el.scrollLeft, el.clientWidth, el.scrollWidth)) stale = false;
  }

  /**
   * A rejected ask retries on its own clock too: a viewer parked motionless
   * at the end fires no scroll events, and without this the shimmer would
   * wait on a wiggle the viewer has no reason to make.
   */
  function scheduleRetry(): void {
    if (retryTimer !== null) clearTimeout(retryTimer);
    retryTimer = setTimeout(() => {
      retryTimer = null;
      measure();
      probe();
    }, MORE_RETRY_MS);
  }

  /**
   * ONE GROWTH, single-flight. In ring mode the window advances a step —
   * asking the caller first when the tape has run out and the category has
   * not — and recycles the left edge to the cap; in children mode it simply
   * runs the contract. A rejection from `more()` is transient by definition
   * here: nothing is marked exhausted, and the next approach asks again.
   */
  let busy = false;
  async function advance(): Promise<void> {
    if (busy) return;
    busy = true;
    let changed = false;
    try {
      if (ring) {
        let plan = growPlan({ lo, hi }, count, exhausted, wrapLicence());
        if (plan.ask) {
          if (more === undefined) {
            ended = true;
          } else {
            if (Date.now() < coolUntil) return;
            asking = true;
            try {
              const answer = await more();
              coolUntil = 0;
              stale = false;
              if (answer === 'end') ended = true;
              else changed = true;
            } catch {
              /* KEEP THE GHOST (`stale`) — unmounting it here is the clamp
                 hop; see the note on `stale`. Retried after the cooldown. */
              stale = true;
              coolUntil = Date.now() + MORE_RETRY_MS;
              scheduleRetry();
              return;
            } finally {
              asking = false;
            }
          }
          /* Re-planned with the licence measured NOW: `exhausted` may have
             just turned true, and whether one wrap overflows is a fact about
             the tape the answer may have lengthened. */
          plan = growPlan({ lo, hi }, items?.length ?? 0, exhausted, wrapLicence());
          if (plan.ask) return;
        }
        if (plan.hi !== hi || plan.lo !== lo) {
          dropped += plan.lo - lo;
          lo = plan.lo;
          hi = plan.hi;
          changed = true;
        }
      } else {
        if (exhausted || more === undefined) return;
        if (Date.now() < coolUntil) return;
        asking = true;
        try {
          const answer = await more();
          coolUntil = 0;
          stale = false;
          if (answer === 'end') {
            ended = true;
            /* Engage the loop IN THE SAME FLUSH the ghost unmounts in: the
               copies append what the ghost gives up and more, so scrollWidth
               never shrinks under a viewer parked at the end. */
            if (loop && copies === 1) engageCopies();
          } else changed = true;
        } catch {
          /* Same bargain as the ring: keep the ghost, cool off, retry. */
          stale = true;
          coolUntil = Date.now() + MORE_RETRY_MS;
          scheduleRetry();
          return;
        } finally {
          asking = false;
        }
      }
    } finally {
      busy = false;
    }
    /* Still near the end after the growth (a very wide window, a hard fling)?
       Look again once the DOM has caught up — but only when something moved,
       or a caller that mis-answers could be asked in a tight loop. */
    if (changed && typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        measure();
        probe();
      });
    }
  }

  /** Bring recycled tiles back as the viewport returns toward the spacer. */
  function restore(): void {
    const win = restorePlan({ lo, hi }, dropped);
    if (win.lo === lo) return;
    dropped -= lo - win.lo;
    lo = win.lo;
    hi = win.hi;
  }

  /**
   * Engage the copies loop, once, the first time a static rail proves it
   * scrolls. The sequence width is measured with one copy mounted, so the
   * count is honest about the actual cards — including discounting the ghost
   * column when a fetch is still showing one, because the ghost leaves and
   * the copies stay.
   *
   * AND ONLY OVER A SEQUENCE THE VIEWPORT CANNOT HOLD. `probe()` checks
   * `scrollable` before calling; the `advance()` path lands here straight
   * off an 'end' answer, where nothing has re-measured — and a rail whose
   * whole content fits must never loop, or its copies render side by side
   * in plain view (the ring's identical rule; see `oneWrapOverflows`).
   */
  function engageCopies(): void {
    const el = track;
    if (!el || typeof getComputedStyle !== 'function') return;
    const { gap, pad } = trackMetrics(el);
    let seq = el.scrollWidth - pad;
    if (asking || stale) {
      const ghost = el.children[el.children.length - 1] as HTMLElement | undefined;
      if (ghost) seq -= ghost.getBoundingClientRect().width + gap;
    }
    if (pad + seq <= el.clientWidth + 8) return;
    const n = copiesFor(el.clientWidth, seq);
    if (n > 1) copies = n;
  }

  /**
   * A RESIZE CAN CHANGE WHAT LOOPING MEANS, and a rail that does not scroll
   * fires no scroll events to notice it with — so the track watches its own
   * box. Wider: a sequence that used to overflow may now FIT, and a window
   * already grown into repeats (or an engaged copies loop) would show the
   * same poster twice in plain view — the ring folds back to exactly one
   * wrap, the copies back to one copy. Narrower: a rail that used to fit
   * now overflows, and `probe()` takes it from there — growth wraps again
   * the moment the geometry says a loop could hide its seam.
   */
  function refit(): void {
    /* The folds are FLUSHED before anything re-measures: `probe()` and
       `measure()` read the live DOM, and reading it with sixty tiles still
       mounted told `engageCopies` the one-copy sequence overflowed a glass
       it fits on — which re-engaged the loop this fold had just undone
       (measured: widen to 5200px folded 60 → 20 and probe grew it straight
       back to 40). Same bargain as `shed()`. */
    if (ring) {
      if (exhausted && hi > count && count > 0 && !oneWrapOverflows()) {
        flushSync(() => {
          dropped = 0;
          lo = 0;
          hi = count;
        });
      }
    } else if (copies > 1 && !copyOverflows()) {
      flushSync(() => {
        copies = 1;
      });
    }
    measure();
    probe();
  }

  $effect(() => {
    const el = track;
    if (!el || typeof ResizeObserver !== 'function') return;
    /* The observer greets every new subject with one callback at its current
       size. That is a mount, not a resize — reacting to it would pre-grow
       every overflowing rail's window on load (probe's nearEnd is true from
       scrollLeft 0 whenever one screen shows most of the track), work the
       scroll probes deliberately leave until the viewer actually moves. */
    let lastWidth = -1;
    const ro = new ResizeObserver((entries) => {
      const width = entries[entries.length - 1]?.contentRect.width ?? -1;
      if (width === lastWidth) return;
      const greeting = lastWidth < 0;
      lastWidth = width;
      if (!greeting) refit();
    });
    ro.observe(el);
    return () => ro.disconnect();
  });

  /* ── The settled-frame corrections ───────────────────────────────────────
   * Never against a finger (a correction mid-drag is the rail yanked out of
   * the hand — the watchOpenTile lesson), and never mid-motion: the timer
   * only survives LOOP_SETTLE_MS of scroll silence, and a momentum fling is
   * anything but silent. Both corrections move identical pixels onto
   * identical pixels, which is what makes them invisible; both are geometry,
   * so reduced motion changes nothing about them.
   */
  let settleTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * The pointers currently down on this track, BY ID — a set, not a boolean,
   * because two fingers land and one lifts: a boolean cleared by the first
   * `pointerup` would run the correction while the second finger still held
   * the rail, which is exactly the yank this guard exists to prevent.
   */
  const heldPointers = new Set<number>();

  function scheduleSettle(): void {
    if (settleTimer !== null) clearTimeout(settleTimer);
    settleTimer = setTimeout(() => {
      settleTimer = null;
      if (heldPointers.size > 0) return;
      if (ring) shed();
      else teleport();
    }, LOOP_SETTLE_MS);
  }

  function pointerDown(event: PointerEvent): void {
    heldPointers.add(event.pointerId);
  }
  function pointerUp(event: PointerEvent): void {
    // Only pointers that went down ON THIS TRACK count; a finger lifted off
    // some other part of the page must not release a hold it never took.
    if (!heldPointers.delete(event.pointerId)) return;
    if (heldPointers.size > 0) return;
    scheduleSettle();
  }

  /**
   * An action rather than an `onpointerdown` attribute, because this is
   * bookkeeping about a gesture, not an interaction of its own — the track
   * stays exactly what it is to the accessibility tree (the same argument as
   * Poster's `watchGesture`).
   */
  function watchHold(el: HTMLElement): { destroy(): void } {
    el.addEventListener('pointerdown', pointerDown, { passive: true });
    return {
      destroy: () => el.removeEventListener('pointerdown', pointerDown)
    };
  }

  $effect(() => {
    if (typeof window === 'undefined') return;
    window.addEventListener('pointerup', pointerUp, { passive: true, capture: true });
    window.addEventListener('pointercancel', pointerUp, { passive: true, capture: true });
    return () => {
      window.removeEventListener('pointerup', pointerUp, true);
      window.removeEventListener('pointercancel', pointerUp, true);
      heldPointers.clear();
      if (settleTimer !== null) clearTimeout(settleTimer);
      if (retryTimer !== null) clearTimeout(retryTimer);
    };
  });

  /**
   * Drop whole wraps out of the spacer, pixel-compensated in the same frame.
   * The tape is periodic in `count`, so a spacer `k·count` columns thinner
   * with a scroll `k·count·stride` shorter shows the same items in the same
   * places — measured off the first tile's own rectangle rather than
   * multiplied out, so an open 16:9 card anywhere in the window cannot skew
   * it.
   */
  function shed(): void {
    const el = track;
    if (!el || !ring || !exhausted || count <= 0) return;
    const k = shedWraps(dropped, count);
    if (k === 0) return;
    const first = el.children[1] as HTMLElement | undefined; // [0] is the spacer while dropped > 0
    if (!first) return;
    const x0 = first.getBoundingClientRect().left;
    flushSync(() => {
      dropped -= k * count;
    });
    const x1 = first.getBoundingClientRect().left;
    el.scrollLeft += x1 - x0;
  }

  /**
   * The copies loop's correction: settled past the first stride, move the
   * scroll back a whole number of strides — carrying the DOM focus to the
   * identical tile of the earlier copy FIRST, with `preventScroll`, so a
   * remote's next press walks on from a tile that is exactly where the eye
   * already is. The stride is measured between the two copies' first
   * children, not derived, for the same skew-proofing as `shed()`.
   */
  function teleport(): void {
    const el = track;
    if (!el || copies <= 1) return;
    const kids = el.children;
    const n = kids.length;
    if (n === 0 || n % copies !== 0) return;
    const per = n / copies;
    const stride = kids[per]!.getBoundingClientRect().left - kids[0]!.getBoundingClientRect().left;
    if (stride <= 0) return;
    const m = teleportWraps(el.scrollLeft, stride);
    if (m === 0) return;

    /* The clone to carry the DOM focus to, found BEFORE anything moves. */
    let carry: HTMLElement | null = null;
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    if (active instanceof HTMLElement && el.contains(active)) {
      let idx = -1;
      for (let i = 0; i < n; i += 1) {
        const kid = kids[i]!;
        if (kid === active || kid.contains(active)) {
          idx = i;
          break;
        }
      }
      const back = idx - m * per;
      if (idx >= 0 && back >= 0) {
        const home = kids[back] as HTMLElement;
        carry = home.matches('a,button,[tabindex]')
          ? home
          : home.querySelector<HTMLElement>('a,button,[tabindex]');
      }
    }
    /*
     * SCROLL FIRST, FOCUS SECOND. Focusing the clone while it is still a
     * stride off the glass invites `focusin`'s own scrollIntoView to "help"
     * — measured: the double correction landed 207px short and the snap then
     * dragged the rail to 0. With the teleport applied first, the clone is
     * already exactly where the eye is and every follow-up is a no-op.
     */
    el.scrollLeft -= m * stride;
    carry?.focus({ preventScroll: true });
  }

  /*
   * THE READS STAY IN THE EVENT, deliberately. A scroll event fires from the
   * rendering steps with layout clean by construction, so `measure`/`probe`
   * read `scrollLeft`/`scrollWidth` for free here — deferring them to a rAF
   * (tried, measured, reverted) lands them AFTER the focus tracker's own rAF
   * has flushed the frame's class writes, where the same reads each force a
   * style-and-layout pass. Timing, not count, is what these reads cost.
   */
  function onScroll(): void {
    measure();
    probe();
    sweepStale();
    scheduleSettle();
  }

  function nudge(direction: 1 | -1): void {
    const el = track;
    if (!el) return;
    const instant = document.documentElement.dataset.tv === 'on' ||
      (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches);
    el.scrollBy({ left: direction * Math.max(240, el.clientWidth * 0.82), behavior: instant ? 'instant' : 'smooth' });
  }

  /**
   * Re-measure whenever the content plausibly changed shape, and fill a
   * viewport so wide the window does not even scroll yet — the ring's answer
   * to the old "reveal immediately when there is nothing to move".
   */
  $effect(() => {
    void slice.length;
    void copies;
    void asking;
    void exhausted;
    measure();
    if (!scrollable) {
      if (ring && !exhausted && count > 0) void advance();
      else if (!ring && !exhausted && more !== undefined) void advance();
    }
  });
</script>

<!-- `strip-host`: the details strip opens while a focused poster is inside —
     see SpotlightStrip.svelte, which owns the strip for every surface now. -->
<section class="rail strip-host" class:ranked>
  <header>
    <div class="head">
      <h2>{title}</h2>
      {#if note}<p class="note">{note}</p>{/if}
    </div>
    <div class="tools">
      {#if href}<a class="more" {href}>{hrefLabel}</a>{/if}
      <div class="arrows">
        <button type="button" onclick={() => nudge(-1)} disabled={atStart} aria-label="Scroll left">
          <Icon name="back" size={17} />
        </button>
        <button type="button" onclick={() => nudge(1)} disabled={atEnd} aria-label="Scroll right">
          <Icon name="forward" size={17} />
        </button>
      </div>
    </div>
  </header>

  <!--
    A RAIL IS ONE D-PAD GROUP. Left and Right on a remote move between the cards
    of THIS row and can never leave it; Up and Down leave it and look for another
    rail, skipping the heading's "All" link and the two scroll arrows that sit in
    the band between two rows. `dpad-rules.ts` explains why that has to be a rule
    and not an emergent property of nearest-neighbour geometry.
  -->
  <div
    class="track"
    class:wide
    data-dpad="row"
    bind:this={track}
    onscroll={onScroll}
    use:watchHold
  >
    {#if ring}
      {#if dropped > 0}
        <!-- Exactly the recycled columns' width, in the same tokens the
             columns are laid out in, so a breakpoint change re-derives both
             sides of the equation and nothing on the glass ever moves. -->
        <div class="ring-spacer" style:--dropped={dropped} bind:this={spacerEl} aria-hidden="true"></div>
      {/if}
      {#each slice as e (e.j)}
        {@render tile?.(e.item, e.ordinal)}
      {/each}
      {#if asking || stale}
        <!-- "More is coming", in the shimmer every rail already speaks —
             while a fetch is in flight, and THROUGH a rejection (`stale`):
             unmounting it at the rail's end is a visible clamp hop. -->
        <Poster ghost />
      {/if}
    {:else if children}
      {#each copyKeys as c (c)}
        {@render children()}
      {/each}
      {#if asking || stale}
        <Poster ghost />
      {/if}
    {/if}
  </div>

  <!--
    THE DETAILS GO UNDER THE ROW, not on the card. A focused tile is 16:9 by
    then and its own two lines of text belong to a shape it has stopped being.
    The strip itself — content, reveal, fluid type — is SpotlightStrip.svelte,
    shared with every grid surface; `inset` because this rail bleeds its track
    to the screen edge, so the strip pays the page pad itself.
  -->
  <SpotlightStrip inset />
</section>

<style>
  /*
   * ── HEADROOM FOR THE FOCUSED CARD ────────────────────────────────────────
   * A focused tile lifts 6px and scales 5%, which put its top edge through the
   * heading above it. Rather than shrink the tile — the growth IS the
   * affordance — the heading steps out of the way.
   *
   * THE HEADING MOVES EXACTLY AS FAR AS THE TILE'S TOP EDGE DOES, on the same
   * 130ms curve the frame lifts on, so the space between the two is CONSTANT
   * through the whole transition — the owner watched the old version (a fixed
   * -9px over 180ms against a rise of 6px + 2.5% of the tile's height over
   * 130ms) and read the mismatch as the gap jumping. Measured before this fix:
   * heading→tile gap went 2.5px → -1.8px on focus (the ring overlapped the
   * heading) and spiked to 11.5px for a frame on the way out.
   *
   * The rise is 6px of translate plus half of the 5% scale (the growth is
   * about the centre, so 2.5% of the height comes out of the top). An OPEN
   * card drops its scale and keeps the 6px, and it does that on the 240ms the
   * expansion runs on — the heading follows on the same clock.
   *
   * `:has()` is what makes this possible without plumbing state up: the rail
   * that CONTAINS the focused card is the one that reacts, and only one card in
   * the app is focused at a time.
   */
  .rail > header {
    transition: transform 130ms var(--ease-out);
  }
  .rail:has(:global(.poster.focused)) > header {
    transform: translateY(calc(-6px - var(--tile-h) * 0.025));
  }
  .rail:has(:global(.poster.open)) > header {
    transform: translateY(-6px);
    transition-duration: 240ms;
  }

  /* The details strip itself — content, reveal, fluid type, TV manners — is
     SpotlightStrip.svelte, one implementation for rails and grids alike. */

  /*
   * A RANKED TILE IS EXACTLY A TILE. The column is the poster width every
   * other rail uses (`--tile-w`) plus `--rank-peek` — the sliver of numeral
   * left showing outside the artwork — and the peek is CONSTANT whatever the
   * digit count. "10" simply sits further behind its poster than "9" does
   * (the owner's stated preference), instead of shouldering the tile
   * sideways: measured before this fix, ranks 1–9 drew 219px-wide tiles and
   * rank 10 drew a 167px one, in a row whose neighbours draw 195. The height
   * comes from `--tile-w` like everywhere else, so the artwork is a true 2:3
   * again rather than the cropped 368px-tall column the old ranked --col
   * produced.
   *
   * --rank-size steps with the viewport rather than sitting at one value, so a
   * phone is not carrying a television's numeral across a 390px screen.
   */
  .rail.ranked {
    --rank-size: 74px;
    --rank-peek: calc(var(--rank-size) * 0.3);
    --col: calc(var(--tile-w) + var(--rank-peek));
  }
  @media (min-width: 560px) {
    .rail.ranked {
      --rank-size: 88px;
    }
  }
  @media (min-width: 900px) {
    .rail.ranked {
      --rank-size: 96px;
    }
  }
  :global(html[data-tv='on']) .rail.ranked {
    --rank-size: 128px;
  }

  /*
   * Reduced motion keeps the INFORMATION and drops the movement: the heading
   * still clears, instantly. (The strip handles its own — SpotlightStrip.)
   */
  @media (prefers-reduced-motion: reduce) {
    .rail > header {
      transition: none;
    }
  }

  /*
   * THE SECOND PASS AT THE SPACE BETWEEN RAILS, because the owner said it twice.
   *
   * The first pass took the margin 26 -> 10 and measured the band between one
   * rail's captions and the next heading at 20.5px. That was the wrong thing to
   * measure: the band is 16.5px of margin AND an empty 44px box the heading sits
   * inside, because `header` carried `min-height: var(--hit)` for the benefit of
   * a 44px "All" link that most rails do not even have. A 27px heading in a 44px
   * box is 17px of nothing, on every rail, on every screen.
   *
   * So the floor moves off the header and onto the two controls that need it —
   * see `.more` and `.arrows button`, which keep their 44px targets and give the
   * excess back as negative margin. The header is now as tall as its heading.
   */
  .rail {
    /*
     * ONE POSTER WIDTH, DECLARED ONCE. `--tile-w` is the width a poster is
     * drawn at; `--col` is the column the track reserves for it — the same
     * number everywhere except a ranked rail, which adds the numeral's peek on
     * top WITHOUT touching the poster (see the ranked block). `--tile-h` and
     * `--tile-open` derive from the poster, never from the column, so a ranked
     * tile is exactly the size of an unranked one.
     *
     * Declared on the RAIL rather than the track so the heading's headroom
     * rule can read `--tile-h` too — the heading has to move exactly as far as
     * the tile's top edge does, and that distance is a function of the tile.
     *
     * 148px is the old 118 grown by the owner's 25%. The min() is the phone
     * clause: an open card is `--tile-w * 8/3` wide and must still FIT between
     * the page pads (390 - 2x14 = 362px), so below ~422px the poster is
     * whatever width keeps the 16:9 expansion exactly inside them — 135.75px
     * on a 390 phone, the full 148 the moment the screen affords it.
     */
    --tile-w: min(148px, calc((100dvw - 2 * var(--page-pad)) * 3 / 8));
    --col: var(--tile-w);
    --tile-h: calc(var(--tile-w) * 3 / 2);
    /*
     * AND A HARD CEILING ON THE OPEN CARD, because the derivation above is a
     * PREDICTION and this is a fact. It assumed `--page-pad` was the real inset
     * and `100vw` the real width; on a notched phone the inset is the safe-area
     * value instead, and `100vw` counts a scrollbar the content cannot use — so
     * a card that was supposed to fit came out wider than the screen and the
     * row scrolled the page sideways. Clamped here, no arithmetic upstream can
     * produce a card wider than the glass.
     */
    --tile-open: min(
      calc(var(--tile-h) * 16 / 9),
      calc(100dvw - 2 * max(var(--page-pad), var(--safe-l), var(--safe-r)))
    );
    margin-block: 6px;
    /*
     * A RAIL CANNOT CHANGE THE SIZE OF ANYTHING OUTSIDE ITSELF, so say so.
     *
     * Moving the remote puts a `focused` class on one card and takes it off
     * another, and without this the browser has to treat that as a change that
     * could reflow the whole 4700px page — thirteen rails re-laid-out because
     * one poster lifted. `contain: layout` scopes it to the rail it happened in,
     * which is the single largest thing left in a keypress once the measuring
     * has been made cheap (see `dpad.ts`).
     *
     * `layout` alone, deliberately. `paint` would clip the 6px lift and the 5%
     * scale a focused card is drawn with; `size` would make the rail measure
     * itself as empty. Neither is needed to get the invalidation scoping.
     */
    contain: layout;
  }

  /* Centre, not baseline: the "All" link is a 44px target and a baseline against
     it would drop the heading half a line. */
  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding-inline: max(var(--page-pad), var(--safe-l)) max(var(--page-pad), var(--safe-r));
  }
  .head {
    min-width: 0;
  }
  h2 {
    font-size: var(--text-lg);
    font-weight: var(--weight-semi);
    letter-spacing: var(--tracking-snug);
  }
  .note {
    margin-top: 1px;
    color: var(--text-faint);
    font-size: var(--text-xs);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .tools {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  /* Defect #16: this was a 19×27 target. A heading link is small by design, so it
     keeps its size and gains its floor as padding.

     THE FLOOR IS A HIT TARGET, NOT A LAYOUT HEIGHT, and the negative block
     margin is what says so. The 44px box a thumb needs is still 44px and still
     centred on the link; it simply overhangs the header rather than setting its
     height, so a row of headings no longer pays 17px of empty box each. It
     overhangs into the track's own 14px of lift padding, where nothing is drawn. */
  .more {
    display: grid;
    place-content: center;
    min-height: var(--hit);
    min-width: var(--hit);
    margin-block: -8px;
    padding-inline: var(--space-2);
    margin-right: calc(var(--space-2) * -1);
    border-radius: var(--radius-pill);
    color: var(--text-dim);
    font-size: var(--text-xs);
    font-weight: var(--weight-semi);
  }
  .more:hover {
    color: var(--text);
  }

  .arrows {
    display: none;
    gap: 4px;
  }
  .arrows button {
    display: grid;
    place-content: center;
    width: 28px;
    height: 28px;
    border-radius: 99px;
    color: var(--text-dim);
    background: var(--surface-2);
    transition:
      background-color 140ms var(--ease),
      color 140ms var(--ease),
      opacity 140ms var(--ease);
  }
  .arrows button:hover:not(:disabled) {
    background: var(--surface-3);
    color: var(--text);
  }
  .arrows button:disabled {
    opacity: 0.28;
    cursor: default;
  }
  /* A tablet is wide enough to draw the arrows and is still driven by a thumb.
     Same bargain as `.more`: the target grows, the header does not. */
  @media (pointer: coarse) {
    .arrows button {
      width: var(--hit);
      height: var(--hit);
      margin-block: -8px;
    }
  }

  /*
   * A REMOTE HAS NO USE FOR A SCROLL ARROW — Right IS the scroll arrow, which is
   * the argument `dpad.ts` makes for skipping them. On a television they are two
   * dead controls per rail that the focus search still has to measure (26 of the
   * 101 candidates on Home) and 44px of header the heading did not need.
   *
   * Note the `@media (pointer: coarse)` above matches a Fire TV Stick — an
   * Android TV WebView reports coarse and hover:none exactly like a phone
   * (`focus.svelte.ts` measured it), which is why this cannot be a media query.
   */
  :global(html[data-tv='on']) .arrows {
    display: none;
  }
  /* The optical -8px that hangs "All" over the page pad is 8px INTO the overscan
     on a television, where the pad is the safe margin and not a margin. */
  :global(html[data-tv='on']) .more {
    margin-right: 0;
  }

  /*
   * ONE COLUMN WIDTH, DECLARED ONCE, AND WHAT A CARD MAY GROW TO.
   *
   * `--col` is the card. `--tile-h` is the height a 2:3 poster comes to at that
   * width, and `--tile-open` is the width the SAME height needs to be 16:9 —
   * so a card that expands into a trailer keeps its height exactly and only
   * ever grows sideways, into a scroller that can absorb it. A tile reads these
   * and a page that is not a rail declares none of them, which is how a
   * wrapping grid opts out of the whole idea (see `Poster.svelte`).
   *
   * FLEX RATHER THAN GRID, for one reason: a grid track's width belongs to the
   * track, so an item cannot widen itself. A flex item can, its neighbours
   * slide out of the way, and the row's scrollWidth grows with it — which is
   * the difference between "the neighbours move" and "the neighbours are
   * covered up".
   */
  .track {
    /* Geometry (--tile-w, --col, --tile-h, --tile-open) is declared on .rail —
       see the note there. The track only lays the columns out. */
    display: flex;
    gap: var(--gap);
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scroll-snap-type: x proximity;
    /*
     * THE RING AND THE LOOP EDIT THE CONTENT AROUND THE VIEWPORT — a spacer
     * resizing behind it, tiles restoring into that spacer, wraps shed with
     * their own pixel compensation. Every one of those edits is arranged to
     * be geometrically invisible, and the browser's scroll anchoring second-
     * guessing them with its own adjustment is the one way they could become
     * visible. It stands down.
     */
    overflow-anchor: none;
    /*
     * A rail bleeds to the screen edge on purpose — the cut-off poster is what
     * says the row continues — but on a television the screen edge is not on the
     * screen. Through the safe tokens (which are 0 everywhere else, so nothing
     * moves on a phone or a desktop) the bleed stops at the overscan line
     * instead of behind it, and the first poster comes back.
     */
    scroll-padding-inline: max(var(--page-pad), var(--safe-l)) max(var(--page-pad), var(--safe-r));
    padding-inline: max(var(--page-pad), var(--safe-l)) max(var(--page-pad), var(--safe-r));
    /*
     * ROOM FOR THE LIFT. `overflow-x: auto` makes the cross axis `auto` too, so
     * a focused card that grows by 5% and rises 6px is CLIPPED by the track it
     * lives in — and the clip is at the top, where the rim is. This is the only
     * place that pays for it; the card never sets a margin of its own.
     *
     * 20px, because the overhang is the RING'S overhang, not the frame's: 6px
     * of rise plus 2.5% of a poster up to 293px tall (7.3px) plus the 2px ring
     * itself plus 2.5px on a television. The old 14px covered the frame and
     * clipped 1.3px off a 2px ring's top edge — which is why the owner said
     * the focus ring was "not always visible": on every focused tile its top
     * line was two-thirds gone. The margins hand back all but 2px of the top
     * and all of the bottom, so the page's rhythm never sees this padding.
     */
    padding-block: 20px 8px;
    margin-block: -18px -8px;
    scrollbar-width: none;
  }
  .track::-webkit-scrollbar {
    display: none;
  }
  .track > :global(*) {
    /* `0 0 auto` with an explicit basis, not `flex: 0 0 var(--col)`: a card that
       expands sets its own basis and must be able to win. Nothing here may
       shrink, or a rail of twenty would squeeze itself to fit instead of
       scrolling. */
    flex: 0 0 var(--col);
    scroll-snap-align: start;
  }
  /*
   * THE RECYCLED COLUMNS, AS ONE BOX. `k` dropped columns occupied
   * `k·(--col + --gap) − --gap` of content (a gap follows each column, and
   * the flex gap after the spacer itself pays the last one back), so the
   * spacer states exactly that — in the same custom properties the columns
   * are laid out in, which is what keeps recycling pixel-exact across every
   * breakpoint without a script measuring anything.
   */
  .track > .ring-spacer {
    flex: 0 0 calc(var(--dropped) * (var(--col) + var(--gap)) - var(--gap));
    scroll-snap-align: none;
  }
  /*
   * AN EXPANDING CARD MUST STAY A SNAP TARGET, and this is the opposite of what
   * it looks like it should be.
   *
   * Chromium re-snaps a container whenever its layout changes, so a card
   * growing from 156 px to 416 px triggers one. Taking `scroll-snap-align` off
   * the open card — the obvious "don't snap to the thing that is moving" — made
   * the browser choose the NEXT card instead: measured at 1440x900, scrollLeft
   * jumped 0 → 168 the instant the card opened, which put the pointer over card
   * 1, which moved the focus, which closed the card, which snapped it back. A
   * card that opened and shut twice a second. Left as a target, the re-snap
   * lands on the same left edge the card already had and scrollLeft stays 0
   * through the whole expansion.
   */
  /* A resume card is a 16:9 still and its own width; 78vw on a phone leaves the
     next one showing, which is what says the row continues. */
  .track.wide {
    --col: min(78vw, 300px);
  }

  @media (min-width: 560px) {
    .rail {
      --tile-w: 172px;
    }
    .track.wide {
      --col: 325px;
    }
  }
  @media (min-width: 900px) {
    .rail {
      --tile-w: 195px;
      margin-block: 8px;
    }
    .track.wide {
      --col: 362px;
    }
    .arrows {
      display: flex;
    }
  }

  @media (max-width: 767px) {
    .rail {
      /* A quarter-screen poster leaves the next title visible under a thumb.
         Height, expansion and ring spacers all keep deriving from this width. */
      --tile-w: min(25dvw, 148px);
      --gap: 8px;
    }
  }

  /*
   * THE TELEVISION COLUMN, and it is the SECOND half of a two-part answer.
   *
   * The first half is the layout viewport: a 1080p stick used to lay out at 960
   * CSS px, so this rail's 195px column was painted 390 pixels wide — a fifth of
   * the panel, four and a half posters across the whole screen. At 1280 CSS px
   * (`tv.ts`) the same 195px column is 292 real pixels and 5.7 fit. Most of
   * "too zoomed in" and "the rails are too large" is already answered there.
   *
   * 172px is what is left of the owner's "a bit too large": it is not a new
   * number but the one this rail already uses between 560 and 900, so a
   * television gets a column the design has already been drawn at. It lands at
   * 258 real pixels — a third narrower than today — and 6.4 posters across.
   * Phones and tablets keep every pixel of theirs; the complaint there was the
   * gap, not the tile.
   */
  :global(html[data-tv='on']) .rail {
    --tile-w: 172px;
  }
  :global(html[data-tv='on']) .track.wide {
    --col: 325px;
  }
</style>
